import { useState } from 'react';
import { queryClient, queryKeys } from './query-client';
import { API_BASE } from './api-base';
import { apiFetch } from './api-client';
import { filePath, nameOf, writeDirectoryOf, writeKeyOf, type BrowsableFile } from './file-browser';

// The client half of "Move to…": POST the two paths, then make both the old and the new workspace re-read.
//
// The rule about WHICH moves are legal is not here — it is `planFileMove` in common-lib, which the route and
// the native bridge both run, so a client cannot talk any surface into an illegal move by asking nicely.
// This module only asks, reports, and refreshes.

export type FileMoveState = 'idle' | 'moving' | 'error';

/** The destination path a file lands at when moved into `folder`: the folder plus the file's own name. */
export function movedPath(item: BrowsableFile, folder: string): string {
  return `${folder}/${nameOf(item.name)}`;
}

export function useFileMove() {
  const [state, setState] = useState<FileMoveState>('idle');

  /**
   * Moves `item` into `folder`. Resolves to true on success.
   *
   * `edits` is how a RENAME from the editor travels: a new filename, and the body and description the user
   * changed in the same breath. One call, so the write-then-delete ordering the route documents covers the
   * whole act rather than a move racing an edit.
   */
  const move = async (
    item: BrowsableFile,
    folder: string,
    edits?: { name?: string; content?: string; hint?: string },
  ): Promise<boolean> => {
    // The KEYS inside the directory, not the tree paths — see `writeKeyOf`. An agent's file is
    // `brain/<agent>/…` in the browser and `<agent>/…` in the store, and moving the tree path nested a
    // second `brain` inside the first.
    const directory = writeDirectoryOf(folder) ?? undefined;
    const fromPath = writeKeyOf(filePath(item));
    const renamed = edits?.name ? { ...item, name: edits.name } : item;
    const toPath = writeKeyOf(movedPath(renamed, folder));
    setState('moving');
    try {
      const res = await apiFetch(`${API_BASE}/api/move-file`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({
          fromPath,
          toPath,
          ...(directory ? { directory } : {}),
          ...(edits?.content !== undefined ? { content: edits.content } : {}),
          ...(edits?.hint ? { hint: edits.hint } : {}),
        }),
      });
      if (!res.ok) {
        setState('error');
        return false;
      }
      // BOTH workspaces, because a move may cross them: the file left one list and joined another, and
      // invalidating only the source would leave the destination stale until something else refetched it.
      // `item.workspaceId` is the source; the destination is the first segment of the new path.
      const destination = toPath.split('/')[0];
      await queryClient.invalidateQueries({ queryKey: queryKeys.workspaceFiles(item.workspaceId) });
      if (destination && destination !== item.workspaceId) {
        await queryClient.invalidateQueries({ queryKey: queryKeys.workspaceFiles(destination) });
      }
      // And the account-wide list, which is its own cache entry (the cross-workspace `/files` browser
      // and the Files summary tiles read it) — a per-workspace key never reaches it.
      await queryClient.invalidateQueries({ queryKey: queryKeys.allWorkspaceFiles });
      setState('idle');
      return true;
    } catch {
      setState('error');
      return false;
    }
  };

  return { state, move, reset: () => setState('idle') };
}
